import { api } from "@/redux/api";

import { ToDoTagInfo, ToDoTagInfoSchema } from "@/schemas/tag.schema";

import {
	Assignee,
	CreateSubTaskInfo,
	CreateTaskInfo,
	DeleteTaskResponse,
	DeleteTaskResponseSchema,
	SubTaskInfo,
	SubTaskInfoSchema,
	TaskInfo,
	TaskInfoSchema,
	UpdateSubTaskInfo,
	UpdateTaskInfo,
	UserTasksResponseSchema,
} from "../../pages/TasksAndSubTasks/schema/tasks-and-subtasks";

export const tasksAndSubtasksApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getAllTasks: builder.query<TaskInfo[], { search?: string } | void>({
			query: (params) => ({ url: "/tasks/all", params: { search: params?.search || undefined } }),
			providesTags: ["TasksAndSubTasks"],
		}),
		getAssignees: builder.query<Assignee[], void>({
			query: () => {
				return {
					url: `users/assignee`,
				};
			},
			providesTags: ["Users"],
		}),
		getAllTasksByUserId: builder.query<(TaskInfo | SubTaskInfo)[], number | null>({
			query: (userId) => ({
				url: `tasks/${userId}`,
				responseSchema: UserTasksResponseSchema,
			}),
			providesTags: ["TasksAndSubTasks"],
		}),
		getAllUnassignedTasksAndSubtasks: builder.query<(TaskInfo | SubTaskInfo)[], void>({
			query: () => ({
				url: "tasks/unassigned",
				responseSchema: UserTasksResponseSchema,
			}),
			providesTags: ["TasksAndSubTasks"],
		}),
		getAllSubTasks: builder.query<SubTaskInfo[], { search?: string } | void>({
			query: (params) => ({
				url: "/subtasks/all",
				params: { search: params?.search || undefined },
			}),
			providesTags: ["TasksAndSubTasks"],
		}),

		getTaskById: builder.query<TaskInfo, number>({
			query: (id) => ({
				url: `tasks/${id}`,
				responseSchema: TaskInfoSchema,
			}),
			providesTags: (_result, _error, id) => [{ type: "TasksAndSubTasks", id }],
		}),

		createTask: builder.mutation<TaskInfo, CreateTaskInfo>({
			query: (body) => ({
				url: "tasks",
				method: "POST",
				body,
				responseSchema: TaskInfoSchema,
			}),
			invalidatesTags: ["TasksAndSubTasks"],
		}),

		updateTask: builder.mutation<TaskInfo, UpdateTaskInfo>({
			query: ({ id, ...body }) => ({
				url: `tasks/${id}`,
				method: "PATCH",
				body,
				responseSchema: TaskInfoSchema,
			}),
			invalidatesTags: (_result, _error, { id }) => [
				"TasksAndSubTasks",
				{ type: "TasksAndSubTasks", id },
			],
		}),

		deleteTask: builder.mutation<DeleteTaskResponse, number>({
			query: (id) => ({
				url: `tasks/${id}`,
				method: "DELETE",
				responseSchema: DeleteTaskResponseSchema,
			}),
			invalidatesTags: ["TasksAndSubTasks"],
		}),

		createSubTask: builder.mutation<SubTaskInfo, CreateSubTaskInfo>({
			query: ({ taskId, ...body }) => ({
				url: `subtasks/${taskId}`,
				method: "POST",
				body,
				responseSchema: SubTaskInfoSchema,
			}),
			invalidatesTags: (_result, _error, { taskId }) => [
				"TasksAndSubTasks",
				{ type: "TasksAndSubTasks", id: taskId },
			],
		}),

		updateSubTask: builder.mutation<SubTaskInfo, UpdateSubTaskInfo>({
			query: ({ taskId, id, ...body }) => ({
				url: `subtasks/${taskId}/${id}`,
				method: "PATCH",
				body,
				responseSchema: SubTaskInfoSchema,
			}),
			invalidatesTags: (_result, _error, { taskId }) => [
				"TasksAndSubTasks",
				{ type: "TasksAndSubTasks", id: taskId },
			],
		}),

		deleteSubTask: builder.mutation<DeleteTaskResponse, { taskId: number; id: number }>({
			query: ({ taskId, id }) => ({
				url: `subtasks/${taskId}/${id}`,
				method: "DELETE",
				responseSchema: DeleteTaskResponseSchema,
			}),
			invalidatesTags: (_result, _error, { taskId }) => [
				"TasksAndSubTasks",
				{ type: "TasksAndSubTasks", id: taskId },
			],
		}),

		getAllTags: builder.query<ToDoTagInfo[], void>({
			query: () => ({
				url: "tasks/tags",
				responseSchema: ToDoTagInfoSchema,
			}),
			providesTags: ["ToDoTags"],
		}),
	}),
});

export const {
	useGetAllTasksQuery,
	useGetAssigneesQuery,
	useGetTaskByIdQuery,
	useGetAllSubTasksQuery,
	useGetAllTasksByUserIdQuery,
	useGetAllUnassignedTasksAndSubtasksQuery,
	useCreateTaskMutation,
	useUpdateTaskMutation,
	useDeleteTaskMutation,
	useCreateSubTaskMutation,
	useUpdateSubTaskMutation,
	useDeleteSubTaskMutation,
	useGetAllTagsQuery,
} = tasksAndSubtasksApi;

export const tasksAndSubtasksReducer = tasksAndSubtasksApi.reducer;
export const tasksAndSubtasksMiddleware = tasksAndSubtasksApi.middleware;
